import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaBolt, FaLightbulb, FaCalculator, FaChartLine } from 'react-icons/fa';

const ElectricFieldCalculator = () => {
  const [charge, setCharge] = useState('');
  const [distance, setDistance] = useState('');
  const [fieldStrength, setFieldStrength] = useState('');
  const [showGraph, setShowGraph] = useState(false);
  const k = 8.99e9; // Coulomb's constant

  const calculateField = () => {
    if (charge && distance) {
      const Q = parseFloat(charge);
      const r = parseFloat(distance);

      // Calculate electric field using E = k * Q / r^2
      const E = k * Q / (r * r);
      setFieldStrength(E.toFixed(2));
      setShowGraph(true);
    }
  };

  const generateGraphData = () => {
    if (!charge || !distance || !fieldStrength) return null;

    const Q = parseFloat(charge);
    const r = parseFloat(distance);
    const E = parseFloat(fieldStrength);

    // Generate data for field strength vs distance
    const series = [
      {
        name: 'Field Strength vs Distance',
        data: Array.from({ length: 20 }, (_, i) => {
          const currentDistance = (i - 10) * (r / 10);
          const currentField = currentDistance !== 0 ? k * Q / (currentDistance * currentDistance) : E;
          return {
            x: currentDistance,
            y: currentField
          };
        }),
      },
    ];

    const options = {
      chart: {
        type: 'line',
        height: 400,
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      xaxis: {
        type: 'numeric',
        title: {
          text: 'Distance (m)',
        },
        labels: {
          formatter: function (val) {
            return val.toFixed(1);
          }
        }
      },
      yaxis: {
        title: {
          text: 'Electric Field (N/C)',
        },
        labels: {
          formatter: function (val) {
            return val.toFixed(2);
          }
        }
      },
      title: {
        text: 'Electric Field vs Distance',
        align: 'center',
      },
      markers: {
        size: 4,
        colors: ['#FF4560'],
      },
      annotations: {
        xaxis: [
          {
            x: 0,
            borderColor: '#FF4560',
            label: {
              borderColor: '#FF4560',
              style: {
                color: '#fff',
                background: '#FF4560',
              },
              text: '0m'
            }
          },
          {
            x: r,
            borderColor: '#FF4560',
            label: {
              borderColor: '#FF4560',
              style: {
                color: '#fff',
                background: '#FF4560',
              },
              text: 'Distance = ' + r + 'm'
            }
          }
        ],
        yaxis: [
          {
            y: E,
            borderColor: '#FF4560',
            label: {
              borderColor: '#FF4560',
              style: {
                color: '#fff',
                background: '#FF4560',
              },
              text: 'Field = ' + E.toFixed(2) + 'N/C'
            }
          }
        ]
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val.toFixed(2) + 'N/C';
          }
        }
      }
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-yellow-50 to-orange-50">
      <Helmet>
        <title>Electric Field Calculator | Physics Tool</title>
        <meta name="description" content="Calculate electric field strength using charge and distance. Interactive calculator with line graph visualization showing field distribution." />
        <meta name="keywords" content="electric field calculator, physics calculator, electromagnetism calculator, electrical calculator" />
        <meta property="og:title" content="Electric Field Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate electric field strength using charge and distance. Interactive calculator with line graph visualization showing field distribution." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Electric Field Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate electric field strength using charge and distance. Interactive calculator with line graph visualization showing field distribution." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/electric-field" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-yellow-600 mb-4">
            <FaBolt />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Electric Field Calculator</h1>
          <p className="text-xl text-gray-600">Calculate electric field strength using charge and distance</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Charge (C)</label>
                <input
                  type="number"
                  value={charge}
                  onChange={(e) => setCharge(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter charge in coulombs (C)"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Distance (m)</label>
                <input
                  type="number"
                  value={distance}
                  onChange={(e) => setDistance(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter distance in meters (m)"
                />
              </div>
              <div>
                <button
                  onClick={calculateField}
                  className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Field
                </button>
              </div>

              {charge && distance && fieldStrength && (
                <div className="mt-4 p-4 bg-yellow-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-yellow-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-yellow-800">Results</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-yellow-800">Charge = {charge} C</p>
                        <p className="text-yellow-800">Distance = {distance} m</p>
                        <p className="text-yellow-800">Electric Field = {fieldStrength} N/C</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-yellow-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using the electric field formula:
                      <br />
                      E = k * Q / r²
                      <br />
                      <br />
                      Where:
                      <br />
                      E = Electric Field = {fieldStrength} N/C
                      <br />
                      k = Coulomb's constant = 8.99 × 10⁹ N·m²/C²
                      <br />
                      Q = Charge = {charge} C
                      <br />
                      r = Distance = {distance} m
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Field Distribution</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="line"
                height={400}
              />
              <p className="mt-4 text-gray-600 text-center">
                Shows how electric field strength changes with distance
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter the charge in coulombs (C)</li>
            <li>Enter the distance in meters (m)</li>
            <li>Click "Calculate Field" to get the result</li>
            <li>View the line graph showing field distribution</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Electric Field Calculation</h3>
              <p>Calculate field strength using Coulomb's law</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Interactive Visualization</h3>
              <p>View how field strength changes with distance</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for physics and electromagnetism students</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Step-by-Step Solutions</h3>
              <p>See how calculations are performed</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is Electric Field?</h3>
              <p className="text-gray-600">
                Electric field is a vector field that describes the force that would be exerted on a unit positive test charge at every point in space.
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What units should I use?</h3>
              <p className="text-gray-600">
                For accurate calculations:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Charge should be in coulombs (C)</li>
                  <li>Distance should be in meters (m)</li>
                  <li>The result will be in newtons per coulomb (N/C)</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">How does the line graph work?</h3>
              <p className="text-gray-600">
                The line graph shows:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>The relationship between electric field and distance</li>
                  <li>Field strength is calculated using E = k * Q / r²</li>
                  <li>Helps visualize how field strength decreases with distance</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are some real-world applications?</h3>
              <p className="text-gray-600">
                Electric field calculations are important in many areas:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Electrostatics and capacitors</li>
                  <li>Particle accelerators</li>
                  <li>Electromagnetic devices</li>
                  <li>Research and development</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Engineers design electrical devices</li>
                  <li>Students understand electric field concepts</li>
                  <li>Teachers demonstrate physics principles</li>
                  <li>Anyone working with charged particles</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElectricFieldCalculator;
